// In GeneratedVideoItem.tsx, update the component to handle renaming properly:
import React, { useState } from "react";
import {
  Tooltip,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  Box,
  Menu,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import GoogleIcon from "@mui/icons-material/Google";
import RefreshIcon from "@mui/icons-material/Refresh";
import SubtitlesIcon from "@mui/icons-material/Subtitles";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { Download, PlayArrow } from "@mui/icons-material";
import { THEME_COLORS } from "../../../context/constants";
import VideoDialog from "../../VideoDialog";
import { GeneratedVideo, IVAvatarVideo } from "../../../context/types";
import SubmagicCaptionModal from "../../SubmagicCaptionModal";
import useStyles from "./styles";

interface GeneratedVideoItemProps {
  video: GeneratedVideo | IVAvatarVideo;
  type: "regular" | "avatarLab";
  onRename?: (videoId: string, newName: string) => void;
  onRefresh?: (videoId: string) => void;
  onDownload?: (video: GeneratedVideo | IVAvatarVideo) => void;
  onDownloadCaptions?: (video: GeneratedVideo | IVAvatarVideo, format: 'ass' | 'srt' | 'txt' | 'svg') => void;
  onUploadToDrive?: (video: GeneratedVideo | IVAvatarVideo) => void;
  onDelete?: (videoId: string) => void;
  onPlay?: (videoUrl: string, captionUrl?: string) => void;
  videoBeingRenamed?: string | null;
  videoBeingRefreshed?: string | null;
  videoBeingDeleted?: string | null;
}

const GeneratedVideoItem: React.FC<GeneratedVideoItemProps> = ({
  video,
  type,
  onRename,
  onRefresh,
  onDownload,
  onDownloadCaptions,
  onUploadToDrive,
  onDelete,
  onPlay,
  videoBeingRenamed,
  videoBeingRefreshed,
  videoBeingDeleted,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newVideoName, setNewVideoName] = useState("");
  const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [captionUrl, setCaptionUrl] = useState("");
  const [captionMenuAnchor, setCaptionMenuAnchor] = useState<null | HTMLElement>(null);
  const [isSubmagicModalOpen, setIsSubmagicModalOpen] = useState(false);
  const [localVideo, setLocalVideo] = useState<GeneratedVideo | null>(
    type === "regular" ? (video as GeneratedVideo) : null
  );
  const classes = useStyles();

  const handleOpenVideoDialog = (url: string, caption?: string) => {
    setVideoUrl(url);
    setCaptionUrl(caption || "");
    setIsVideoDialogOpen(true);
  };

  const handleCloseVideoDialog = () => {
    setIsVideoDialogOpen(false);
    setVideoUrl("");
    setCaptionUrl("");
  };

  const handleStartEditing = () => {
    setNewVideoName(getVideoName());
    setIsEditing(true);
  };

  const handleSaveRename = () => {
    if (newVideoName.trim() && onRename) {
      onRename(video._id, newVideoName.trim());
      setIsEditing(false);
    }
  };

  const handleCancelRename = () => {
    setIsEditing(false);
    setNewVideoName("");
  };

  const handleOpenCaptionMenu = (event: React.MouseEvent<HTMLElement>) => {
    setCaptionMenuAnchor(event.currentTarget);
  };

  const handleCloseCaptionMenu = () => {
    setCaptionMenuAnchor(null);
  };


  const handleDownloadCaptionFormat = (format: 'ass' | 'srt' | 'txt' | 'svg') => {
    if (onDownloadCaptions) {
      onDownloadCaptions(video, format);
    }
    handleCloseCaptionMenu();
  };

  const hasCaptions = () => {
    if (type === "regular") {
      const regularVideo = video as GeneratedVideo;
      return !!regularVideo.caption_url;
    }
    return false;
  };

  const regularVideo = type === "regular" ? (video as GeneratedVideo) : null;


  const getVideoUrl = () => {
    if (type === "regular") {
      const regularVideo = video as GeneratedVideo;
      return regularVideo.withCaptions
        ? regularVideo.video_url_caption
        : regularVideo.video_url;
    } else {
      const ivVideo = video as IVAvatarVideo;
      return ivVideo.video_url || "";
    }
  };

  const getVideoName = () => {
    if (type === "regular") {
      const regularVideo = video as GeneratedVideo;
      return regularVideo.name || "Untitled Video";
    } else {
      const ivVideo = video as IVAvatarVideo;
      return ivVideo.name || ivVideo.video_title || "Avatar Lab Video";
    }
  };

  const isRenaming = videoBeingRenamed === video._id;

  return (
    <div className={classes.generatedVideoItem}>
      {/* Avatar Lab Badge for IV Avatar Videos */}
      {type === "avatarLab" && (
        <div
          style={{
            position: "absolute",
            top: "8px",
            left: "8px",
            zIndex: 5,
            padding: "2px 6px",
            borderRadius: "8px",
            fontSize: "10px",
            fontWeight: "bold",
            backgroundColor: "#9C27B0",
            color: "white",
          }}
        >
          Avatar Lab
        </div>
      )}

      {/* Status Ball */}
      <Tooltip title={`Status: ${video.status}`}>
        <div
          style={{
            width: "12px",
            height: "12px",
            borderRadius: "50%",
            backgroundColor:
              video.status === "completed"
                ? "green"
                : video.status === "failed"
                ? "red"
                : THEME_COLORS.yellow,
            flexShrink: 0,
          }}
        />
      </Tooltip>

      {/* Video Thumbnail */}
      <div className={classes.videoWrapper}>
        <video
          src={getVideoUrl()}
          controls
          className={classes.videoPlayer}
          style={{
            opacity: video.status === "completed" ? 1 : 0.3,
          }}
        />

        {/* Conditional cover for 'processing' and 'failed' */}
        {video.status !== "completed" && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              borderRadius: "4px",
              backgroundColor:
                video.status === "failed"
                  ? THEME_COLORS.darkGray
                  : THEME_COLORS.yellow,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "white",
              fontWeight: "bold",
              fontSize: "10px",
              textTransform: "uppercase",
              textAlign: "center",
              padding: "4px",
            }}
          >
            {video.status === "failed" ? "Failed" : "Processing"}
          </div>
        )}

        {video.status === "completed" && getVideoUrl() && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <IconButton
              onClick={() => handleOpenVideoDialog(getVideoUrl() || "")}
              sx={{
                color: "#fff",
                backgroundColor: "rgba(0,0,0,0.5)",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.7)",
                },
                padding: "4px",
              }}
              size="small"
            >
              <PlayArrow fontSize="small" />
            </IconButton>
          </div>
        )}
      </div>

      {/* Content Area - Name and Actions */}
      <div className={classes.contentWrapper}>
        <div className={classes.topRow}>
          <div className={classes.nameSection}>
            {isEditing ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  value={newVideoName}
                  onChange={(e) => setNewVideoName(e.target.value)}
                  size="small"
                  variant="outlined"
                  autoFocus
                  disabled={isRenaming}
                  sx={{ width: "150px" }}
                />
                <IconButton
                  onClick={handleSaveRename}
                  disabled={!newVideoName.trim() || isRenaming}
                  size="small"
                >
                  {isRenaming ? (
                    <CircularProgress size={16} />
                  ) : (
                    <SaveIcon fontSize="small" />
                  )}
                </IconButton>
                <IconButton
                  onClick={handleCancelRename}
                  size="small"
                  disabled={isRenaming}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">
                  {isRenaming ? "Renaming..." : getVideoName()}
                </Typography>
                {!isRenaming && (
                  <IconButton onClick={handleStartEditing} size="small">
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            )}
          </div>

          {/* Action Buttons */}
          <div className={classes.actionsSection}>
            {video.status !== "completed" && (
              <Tooltip title="Refresh video status">
                <button
                  onClick={() => onRefresh?.(video.heygenId)}
                  className={classes.reloadButton}
                  disabled={videoBeingRefreshed === video.heygenId}
                >
                  {videoBeingRefreshed === video.heygenId ? (
                    <CircularProgress size={18} />
                  ) : (
                    <RefreshIcon
                      sx={{
                        width: "18px",
                        height: "18px",
                      }}
                    />
                  )}
                </button>
              </Tooltip>
            )}

            {/* Download Button */}
            {video.status === "completed" && (
              <Tooltip title="Download video">
                <button
                  onClick={() => onDownload?.(video)}
                  className={classes.videoActionButton}
                >
                  <Download
                    sx={{
                      width: "18px",
                      height: "18px",
                    }}
                  />
                </button>
              </Tooltip>
            )}

            {/* Submagic Captions Button — available for completed regular videos */}
            {video.status === "completed" && type === "regular" && (
              <Tooltip title="Submagic captions">
                <button
                  onClick={() => setIsSubmagicModalOpen(true)}
                  className={classes.videoActionButton}
                >
                  <AutoFixHighIcon sx={{ width: "18px", height: "18px" }} />
                </button>
              </Tooltip>
            )}

            {/* Download Captions Button */}
            {video.status === "completed" && hasCaptions() && (
              <>
                <Tooltip title="Download captions">
                  <button
                    onClick={handleOpenCaptionMenu}
                    className={classes.videoActionButton}
                  >
                    <SubtitlesIcon
                      sx={{
                        width: "18px",
                        height: "18px",
                      }}
                    />
                  </button>
                </Tooltip>
                <Menu
                  anchorEl={captionMenuAnchor}
                  open={Boolean(captionMenuAnchor)}
                  onClose={handleCloseCaptionMenu}
                >
                  <MenuItem onClick={() => handleDownloadCaptionFormat('ass')}>
                    ASS (Original)
                  </MenuItem>
                  <MenuItem onClick={() => handleDownloadCaptionFormat('srt')}>
                    SRT (SubRip)
                  </MenuItem>
                  <MenuItem onClick={() => handleDownloadCaptionFormat('txt')}>
                    TXT (Plain Text)
                  </MenuItem>
                  <MenuItem onClick={() => handleDownloadCaptionFormat('svg')}>
                    SVG (Visual)
                  </MenuItem>
                </Menu>
              </>
            )}


            {video.status === "completed" && (
              <Tooltip title="Upload to Google Drive">
                <button
                  onClick={() => onUploadToDrive?.(video)}
                  className={classes.videoActionButton}
                >
                  <GoogleIcon
                    sx={{
                      width: "18px",
                      height: "18px",
                    }}
                  />
                </button>
              </Tooltip>
            )}

            <Tooltip title="Delete video">
              <button
                onClick={() => onDelete?.(video._id)}
                className={classes.videoActionButton}
                disabled={videoBeingDeleted === video._id}
              >
                {videoBeingDeleted === video._id ? (
                  <CircularProgress size={18} />
                ) : (
                  <DeleteIcon
                    sx={{
                      width: "18px",
                      height: "18px",
                    }}
                  />
                )}
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Video Dialog */}
      {isVideoDialogOpen && (
        <VideoDialog
          videoUrl={videoUrl}
          open={isVideoDialogOpen}
          onClose={handleCloseVideoDialog}
          captionUrl={captionUrl}
        />
      )}

      {/* Submagic Caption Modal */}
      {isSubmagicModalOpen && type === "regular" && (
        <SubmagicCaptionModal
          open={isSubmagicModalOpen}
          onClose={() => setIsSubmagicModalOpen(false)}
          video={localVideo || (video as GeneratedVideo)}
          autoGenerate={!(localVideo?.submagicProjectId || (video as GeneratedVideo).submagicProjectId)}
          onVideoUpdate={(updates) => {
            setLocalVideo(prev =>
              prev ? { ...prev, ...updates } : { ...(video as GeneratedVideo), ...updates }
            );
          }}
        />
      )}
    </div>
  );
};

export default GeneratedVideoItem;
